import {
    useCallback, useEffect, useRef, useState
} from 'react';

import { popupSpecsForDes } from 'lib/constants';
import {
    WM_HEARTBEAT_INTERVAL,
    WM_MSG,
    WM_STALE_TIMEOUT,
    createWindowManagerChannel,
    focusWindowByName,
    getTrackedWindow,
    installWindowOpenTracker,
    isChildPopup,
    requestChildWindowFocus
} from 'services/window-manager.service';

const useChildWindows = () => {
    const [windows, setWindows] = useState([]);
    const channelRef = useRef(null);
    const windowsRef = useRef(new Map());

    const syncState = useCallback(() => {
        setWindows(
            Array.from(windowsRef.current.values()).sort((a, b) => b.openedAt - a.openedAt)
        );
    }, []);

    const markClosed = useCallback((id) => {
        const entry = windowsRef.current.get(id);
        if (entry && !entry.closed) {
            entry.closed = true;
            entry.closedAt = Date.now();
            syncState();
        }
    }, [syncState]);

    useEffect(() => {
        if (isChildPopup()) return undefined;
        installWindowOpenTracker();
        const channel = createWindowManagerChannel();
        if (!channel) return undefined;
        channelRef.current = channel;

        channel.onmessage = (event) => {
            const data = event.data || {};
            if (!data.id) return;
            switch (data.type) {
                case WM_MSG.REGISTER:
                    windowsRef.current.set(data.id, {
                        id: data.id,
                        name: data.name || '',
                        title: data.title || 'Window',
                        url: data.url || '',
                        openedAt: data.openedAt || Date.now(),
                        lastSeen: Date.now(),
                        closed: false,
                        closedAt: null,
                        command: data.command || '',
                        line2: data.line2 || ''
                    });
                    syncState();
                    break;
                case WM_MSG.HEARTBEAT: {
                    const existing = windowsRef.current.get(data.id);
                    if (existing) {
                        existing.lastSeen = Date.now();
                        if (data.title) existing.title = data.title;
                        if (data.name && !existing.name) existing.name = data.name;
                        if (data.url) existing.url = data.url;
                        if (data.command) existing.command = data.command;
                        if (data.line2) existing.line2 = data.line2;
                        if (existing.closed) {
                            existing.closed = false;
                            existing.closedAt = null;
                        }
                    } else {
                        windowsRef.current.set(data.id, {
                            id: data.id,
                            name: data.name || '',
                            title: data.title || 'Window',
                            url: data.url || '',
                            openedAt: Date.now(),
                            lastSeen: Date.now(),
                            closed: false,
                            closedAt: null,
                            command: data.command || '',
                            line2: data.line2 || ''
                        });
                    }
                    syncState();
                    break;
                }
                case WM_MSG.UNREGISTER: {
                    const existing = windowsRef.current.get(data.id);
                    if (existing && !existing.closed) {
                        existing.closed = true;
                        existing.closedAt = Date.now();
                        syncState();
                    }
                    break;
                }
                default:
                    break;
            }
        };

        try { channel.postMessage({ type: WM_MSG.REQUEST_ROSTER }); } catch (e) {
            console.log('request roster message error-->', e);
        }

        const prune = setInterval(() => {
            const now = Date.now();
            let changed = false;
            windowsRef.current.forEach((win, id) => {
                if (win.closed || now - win.lastSeen <= WM_STALE_TIMEOUT) return;
                const entry = windowsRef.current.get(id);
                const ref = win.name ? getTrackedWindow(win.name) : null;
                if (ref) {
                    entry.lastSeen = now;
                    return;
                }
                entry.closed = true;
                entry.closedAt = now;
                changed = true;
            });
            if (changed) syncState();
        }, WM_HEARTBEAT_INTERVAL);

        return () => {
            clearInterval(prune);
            try { channel.close(); } catch (e) {
                console.log('channel close error-->', e);
            }
            channelRef.current = null;
        };
    }, [syncState]);

    const clearClosed = useCallback(() => {
        let changed = false;
        windowsRef.current.forEach((win, id) => {
            if (win.closed) {
                windowsRef.current.delete(id);
                changed = true;
            }
        });
        if (changed) syncState();
    }, [syncState]);

    const reopenWindow = useCallback((id) => {
        const entry = windowsRef.current.get(id);
        if (!entry || !entry.url) return;
        try {
            const win = window.open(entry.url, entry.name || `win_${Date.now()}`, popupSpecsForDes);
            if (win) {
                // Drop the old closed entry; the reopened window registers anew.
                windowsRef.current.delete(id);
                syncState();
            }
        } catch (e) {
            console.log('reopen window error-->', e);
        }
    }, [syncState]);

    const closeAll = useCallback(() => {
        // Close via tracked refs first so popups with hung/blocked JS
        // (which can't process channel messages) still get closed.
        windowsRef.current.forEach((win) => {
            if (win.closed) return;
            const ref = getTrackedWindow(win.name);
            if (ref) {
                try { ref.close(); } catch (e) {
                    console.log('close window error-->', e);
                }
            }
        });
        const channel = channelRef.current;
        if (channel) {
            try { channel.postMessage({ type: WM_MSG.CLOSE_ALL }); } catch (e) {
                console.log('close all message error-->', e);
            }
        }
        // Optimistic; a survivor's next heartbeat flips it back to open.
        const now = Date.now();
        windowsRef.current.forEach((win, id) => {
            if (win.closed) return;
            const entry = windowsRef.current.get(id);
            entry.closed = true;
            entry.closedAt = now;
        });
        syncState();
    }, [syncState]);

    const closeWindow = useCallback((id) => {
        const entry = windowsRef.current.get(id);
        if (!entry || entry.closed) return;
        const ref = getTrackedWindow(entry.name);
        if (ref) {
            try { ref.close(); } catch (e) {
                console.log('close window error-->', e);
            }
        }
        const channel = channelRef.current;
        if (channel) {
            try { channel.postMessage({ type: WM_MSG.CLOSE, id }); } catch (e) {
                console.log('close message error-->', e);
            }
        }
        markClosed(id);
    }, [markClosed]);

    const focusWindow = useCallback((id) => {
        const entry = windowsRef.current.get(id);
        if (!entry || entry.closed) return;
        const { name } = entry;
        const ref = name ? getTrackedWindow(name) : null;
        if (ref) {
            try { ref.focus(); } catch (e) {
                console.log('focus window error-->', e);
            }
        }
        // No live ref (e.g. after a reload): re-target the window by name within
        // this user gesture — works for any window this tab can reach.
        if (name && focusWindowByName(name)) return;
        // Cross-tab: reach the window via the origin-wide channel so its own
        // WindowRegistryClient self-focuses, regardless of which tab opened it.
        const channel = channelRef.current;
        if (channel) {
            try { channel.postMessage({ type: WM_MSG.FOCUS, id }); } catch (e) {
                console.log('focus message error-->', e);
            }
        }
        // Last resort: the service worker can reach the window by URL from any
        // tab (and focus it from a notification click).
        requestChildWindowFocus([entry.url], {
            title: `Focus: ${entry.command || entry.title || 'Window'}`,
            body: entry.line2 || ''
        });
    }, []);

    const focusAll = useCallback(() => {
        // URLs we couldn't raise in-gesture; only these need the SW fallback.
        const unreachableUrls = [];
        windowsRef.current.forEach((entry) => {
            if (entry.closed) return;
            const ref = entry.name ? getTrackedWindow(entry.name) : null;
            if (ref) {
                try { ref.focus(); return; } catch (e) {
                    console.log('focus window error-->', e);
                }
            }
            // No live ref (e.g. after a reload): re-target by name within this
            // user gesture before falling back to the cross-tab SW path.
            if (entry.name && focusWindowByName(entry.name)) return;
            if (entry.url) unreachableUrls.push(entry.url);
        });
        if (!unreachableUrls.length) return;
        // Cross-tab windows: reach them via the origin-wide channel so their own
        // WindowRegistryClient self-focuses, regardless of which tab opened them.
        const channel = channelRef.current;
        if (channel) {
            try { channel.postMessage({ type: WM_MSG.FOCUS_ALL }); } catch (e) {
                console.log('focus all message error-->', e);
            }
        }
        // Last resort: the service worker focuses by URL / shows a notification.
        requestChildWindowFocus(unreachableUrls, {
            title: 'Focus all child windows',
            body: `${unreachableUrls.length} window(s)`
        });
    }, []);

    return {
        windows,
        count: windows.filter((w) => !w.closed).length,
        closeAll,
        closeWindow,
        focusWindow,
        focusAll,
        clearClosed,
        reopenWindow
    };
};

export default useChildWindows;
